import * as React from "react";
import { useState } from "react";
import { useDataProvider, Title } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

const YesNoChip = ({ ok, label }) => (
  <Chip
    icon={ok ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
    label={`${label}: ${ok ? "Yes" : "No"}`}
    color={ok ? "success" : "default"}
    variant={ok ? "filled" : "outlined"}
    sx={{ fontWeight: 700 }}
  />
);

const DetailRow = ({ label, value }) =>
  value ? (
    <Box sx={{ display: "flex", gap: 1, py: 0.35 }}>
      <Typography sx={{ minWidth: 130, color: "#6b7280", fontSize: 14 }}>{label}</Typography>
      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 600 }}>{value}</Typography>
    </Box>
  ) : null;

export const ServiceCheckPage = () => {
  const dataProvider = useDataProvider();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null); // null = no search run yet

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await dataProvider.getList("logistics/pincodes", {
        filter: { q },
        pagination: { page: 1, perPage: 10 },
        sort: { field: "pincode", order: "ASC" },
      });
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Could not check serviceability. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runSearch();
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Title title="Service Availability Checker" />
      <Typography component="h1" sx={{ fontSize: 28, fontWeight: 800, color: "#0A4A47", mb: 0.5 }}>
        Service Availability Checker
      </Typography>
      <Typography sx={{ color: "#4b5563", mb: 2.5 }}>
        Check pickup and delivery serviceability by pincode, place name, or city.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Pincode / Place Name / City to search"
          placeholder="e.g. 560093, Kaggadasapura, or Bengaluru"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button
          variant="contained"
          onClick={runSearch}
          disabled={loading || !query.trim()}
          startIcon={loading ? null : <SearchIcon />}
          sx={{ px: 4, whiteSpace: "nowrap" }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Check"}
        </Button>
      </Stack>

      {error ? (
        <Typography sx={{ color: "#b91c1c", fontWeight: 600, mb: 2 }}>{error}</Typography>
      ) : null}

      {results !== null && !loading && results.length === 0 && !error ? (
        <Typography sx={{ color: "#6b7280" }}>
          No matching location found. Try a different pincode, place, or city.
        </Typography>
      ) : null}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
        {(results || []).map((r) => {
          const deliverable = Boolean(r.delivery_status);
          const pickup = Boolean(r.pickup_status) && !r.embargo;
          return (
            <Card
              key={r.id}
              variant="outlined"
              sx={{ borderRadius: 3, flex: "1 1 320px", minWidth: { xs: "100%", sm: 320 }, maxWidth: 420 }}
            >
              <CardContent>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                  {(r.office_name || "Post Office").trim()}
                  {r.pincode ? ` (${r.pincode})` : ""}
                </Typography>
                <Stack direction="row" sx={{ my: 1.5, flexWrap: "wrap", gap: 1 }}>
                  <YesNoChip ok={pickup} label="Pickup" />
                  <YesNoChip ok={deliverable} label="Delivery" />
                  {r.metro ? <Chip label="Metro" color="info" variant="outlined" /> : null}
                  {r.embargo ? <Chip label="Embargo" color="warning" /> : null}
                </Stack>
                <Divider sx={{ my: 1 }} />
                <DetailRow label="Delivery status" value={deliverable ? "Available" : "Not available"} />
                <DetailRow label="Pickup status" value={pickup ? "Available" : "Not available"} />
                <DetailRow label="Taluk" value={r.taluk} />
                <DetailRow label="District" value={r.district_name} />
                <DetailRow label="State" value={r.state_name} />
                <DetailRow label="Region" value={r.region} />
                <DetailRow label="Address" value={r.post_office_address} />
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ServiceCheckPage;
